import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { Suspense, useState, useRef, useEffect } from "react";
import { useProfileData } from "../../hooks/useProfileData";
import StripeCheckoutForm from '../StripeCheckoutForm';
import { getPrices } from "../../utils/api";
import VerifiedIcon from '@mui/icons-material/Verified';
import TrialButton from "./trialButton";
import { wrapPromise } from "../../utils/helper";
import { useTheme } from '@mui/material/styles';

const enableMonetization = process.env.REACT_APP_ENABLE_MONETIZATION === 'true';

interface PremiumPurchaseButtonProps {
  requiredPremiumKey: string;
  redirectURL?: string;
  sampleData?: any;
}

const PremiumPurchaseButton: React.FC<PremiumPurchaseButtonProps> = (props) => {
  const { requiredPremiumKey, redirectURL = "", sampleData = null } = props;
  const { creatorProfileData } = useProfileData();

  // Use Maps to store data and loading states by key
  const loadedDataMap = useRef<Map<string, any>>(new Map());
  const [loadingStates, setLoadingStates] = useState<Map<string, boolean>>(new Map());
  const [isStripeOpen, setIsStripeOpen] = useState<boolean>(false);
  const [stripeItem, setStripeItem] = useState<string>(null);
  const [trialPeriod, setTrialPeriod] = useState<number>(0);
  const [dataPromise, setDataPromise] = useState(null);
  const [, setRefresh] = useState<boolean>(false);
  const hasMounted = useRef<boolean>(false);
  const theme = useTheme();

  useEffect(() => {
    if (sampleData) {
      loadedDataMap.current.set(requiredPremiumKey, sampleData);
      setRefresh((r: boolean) => !r);
    } else if (requiredPremiumKey) {
      setDataPromise(wrapPromise(loadData(requiredPremiumKey)));
    }
    hasMounted.current = true;
  }, [sampleData, requiredPremiumKey]); // Added requiredPremiumKey as dependency

  useEffect(() => {
    if (hasMounted.current && requiredPremiumKey && !sampleData) {
      // Check if we already have data for this key
      if (!loadedDataMap.current.has(requiredPremiumKey)) {
        setDataPromise(wrapPromise(loadData(requiredPremiumKey)));
      }
    }
  }, [requiredPremiumKey, sampleData]);

  const handlePremiumPurchase = () => {
    // disable buttons if just showing test data
    if (!sampleData) {
      setStripeItem(requiredPremiumKey);
      setIsStripeOpen(true);
    }
  };

  const loadData = (premiumKey: string) => {
    // Check if we're already loading this specific key or if we already have data
    const isCurrentlyLoading = loadingStates.get(premiumKey) || false;
    const hasData = loadedDataMap.current.has(premiumKey);

    if (!isCurrentlyLoading && !hasData && premiumKey) {
      // Set loading state and trigger re-render
      setLoadingStates(prev => new Map(prev.set(premiumKey, true)));

      const promise = getPrices([premiumKey])
        .then(res => res.json())
        .then(data => {
          if (data && data[premiumKey]) {
            loadedDataMap.current.set(premiumKey, data[premiumKey]);
          }
          // Trigger re-render after data is loaded
          setRefresh(r => !r);
        })
        .catch(err => {
          console.log(err);
          // Optionally set error state in the map
          loadedDataMap.current.set(premiumKey, null);
        })
        .finally(() => {
          // Clear loading state and trigger re-render
          setLoadingStates(prev => {
            const newMap = new Map(prev);
            newMap.set(premiumKey, false);
            return newMap;
          });
        });

      return promise;
    } else {
      return Promise.resolve();
    }
  };

  const getCurrentData = () => {
    return loadedDataMap.current.get(requiredPremiumKey);
  };

  const isCurrentlyLoading = () => {
    return loadingStates.get(requiredPremiumKey) || false;
  };

  const Loading = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: (theme) => theme.palette.background["paper-elevation-10"],
          border: `1px solid ${theme.palette.background["paper-elevation-0"]}`,
          borderRadius: '5px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1rem',
            paddingBottom: '0.75rem',
            '& img': {
              marginRight: '0.5rem',
              height: '24px',
            }
          }}
        >
          <Typography
            component='h6'
            variant='h6'
            fontWeight={600}
            sx={{
              fontSize: '16px',
              color: (theme) => theme.palette.text.primary,
              textAlign: 'center',
            }}
          >
            {`Subscribe to ${creatorProfileData.fullName}`}
          </Typography>
        </Box>
        <Box
          sx={{
            pl: '1rem',
            pr: '1rem',
            display: 'flex',
          }}
        >
          <CircularProgress size={30} />
          <Typography
            component='h6'
            variant='h6'
            fontWeight={400}
            sx={{
              fontSize: '14px',
              color: (theme) => theme.palette.text.primary,
              marginBottom: '0.75rem',
              textAlign: 'center',
              ml: 1,
            }}
          >
            Loading...
          </Typography>
        </Box>
        <Button
          id={`button-premium-purchase`}
          variant="contained"
          sx={{
            height: '32px !important',
            padding: '6px 12px !important',
            borderRadius: '6px',
            width: '50%',
            fontSize: '14px',
            marginBottom: '1.5rem'
          }}
          fullWidth
          startIcon={<CircularProgress size={30} />}
        >
          Loading...
        </Button>
      </Box>
    );
  };

  const RealButton = () => {
    const currentData = getCurrentData();

    if (!currentData && !isCurrentlyLoading()) {
      if (dataPromise) {
        dataPromise.read();
      }
    }

    let priceStr = "";
    if (currentData) {
      if (currentData.currency === 'usd') priceStr += "$";
      priceStr += currentData.unit_amount / 100;
      if (currentData.type === 'recurring') priceStr += `/${currentData.recurring.interval}`;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: (theme) => theme.palette.background["paper-elevation-10"],
          border: `1px solid ${theme.palette.background["paper-elevation-0"]}`,
          borderRadius: '5px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1rem',
            paddingBottom: '0.75rem',
            '& img': {
              marginRight: '0.5rem',
              height: '24px',
            }
          }}
        >
          <Typography
            component='h6'
            variant='h6'
            fontWeight={600}
            sx={{
              fontSize: '16px',
              color: (theme) => theme.palette.text.primary,
              textAlign: 'center',
            }}
          >
            {currentData?.title || `Subscribe to ${creatorProfileData.fullName}`}
          </Typography>
          <VerifiedIcon sx={{ ml: 0.5, fill: '#3EACEF' }} />
        </Box>
        <Box
          sx={{
            pl: '1rem',
            pr: '1rem',
          }}
        >
          <Typography
            component='h6'
            variant='h6'
            fontWeight={400}
            sx={{
              fontSize: '14px',
              color: (theme) => theme.palette.text.primary,
              marginBottom: '0.75rem',
              textAlign: 'center',
            }}
          >
            {currentData?.description || `Unlock access to ${creatorProfileData.fullName}'s AI persona`}
          </Typography>
        </Box>
        <Box
          display='flex'
          flexDirection={{ xs: 'column', sm: 'row' }}
        >
          <Button
            id={`button-premium-purchase`}
            variant="contained"
            sx={{
              height: '32px !important',
              padding: '6px 12px !important',
              borderRadius: '6px',
              fontSize: '14px',
              marginBottom: { xs: 2, sm: '1.5rem' },
              width: { xs: '100%', sm: 'auto' }
            }}
            onClick={handlePremiumPurchase}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                ml: 2,
                mr: 2
              }}
            >
              <Typography variant='h7' mr={8}>Subscribe</Typography>
              <Typography variant='h7'>{priceStr}</Typography>
            </Box>
          </Button>
          {
            currentData?.metadata?.trial_code &&
            <Box
              ml={{ xs: 0, sm: 1 }}
              width={{ xs: '100%', sm: 'auto' }}
            >
              <TrialButton
                loadedData={{ current: currentData }}
                requiredPremiumKey={requiredPremiumKey}
                setTrialPeriod={setTrialPeriod}
                setStripeItem={setStripeItem}
                setIsStripeOpen={setIsStripeOpen}
              />
            </Box>
          }
        </Box>
        <StripeCheckoutForm
          item={stripeItem}
          isOpen={isStripeOpen}
          setIsOpen={setIsStripeOpen}
          redirectURL={redirectURL || ""}
          isPremium={true}
          trialPeriod={trialPeriod}
        />
      </Box>
    );
  };

  if (!enableMonetization) return <></>;

  return (
    <Suspense fallback={<Loading />}>
      <RealButton />
    </Suspense>
  );
};

export default PremiumPurchaseButton;
